/**
 * SiteTiles — the Sites page for someone who can only LOOK at sites.
 *
 * The full Sites page is a workshop: model files with sizes and versions, E57 sources with
 * coordinate references, the panorama-to-RVT transform diagnostics, 2D maps, CSV imports, ACC
 * sync state. A read-only member saw all of it with the buttons hidden, and still had no way
 * to open a site from the page. For them the page is now what they came for: their sites, one
 * tap each to open in the viewer. Product decision, 2026-09-24.
 *
 * Who counts as read-only is the server's answer, not a role name: `permission_level` on each
 * site (the same answer useSiteWriteAccess reads), plus admin and onboarding work.
 */
import React from 'react';
import { Asset } from '../../services/AssetService';

export interface ReadOnlySitesVisitorInput {
  isAdmin: boolean;
  hasOnboardingWork: boolean;
  /** Admin or site creator (migration 072) — may create a site, so is never "read-only". */
  canCreateSites?: boolean;
  sites: Asset[];
  canManageSite: (site: Asset) => boolean;
}

/** True when this person may not change anything on any of their sites. */
export function isReadOnlySitesVisitor({ isAdmin, hasOnboardingWork, canCreateSites, sites, canManageSite }: ReadOnlySitesVisitorInput): boolean {
  // A site creator needs the page with the Create Site button even before they have a site.
  if (isAdmin || hasOnboardingWork || canCreateSites) return false;
  return !sites.some(canManageSite);
}

interface SiteTilesProps {
  sites: Asset[];
  /** Every site this person has, before search — decides "no sites yet" vs "no match". */
  totalSites: number;
  currentSiteId: string | null;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onOpenSite: (site: Asset) => void;
  t: (key: string, fallback?: string) => string;
  dir: 'rtl' | 'ltr';
}

const SiteTiles: React.FC<SiteTilesProps> = ({
  sites, totalSites, currentSiteId, searchTerm, onSearchChange, onOpenSite, t, dir
}) => (
  <div className="dt-body" dir={dir}>
    <div className="dt-sitetiles__head">
      <h2 className="dt-sitetiles__title">{t('sites', 'Sites')}</h2>
      {totalSites > 0 && (
        <input
          type="search"
          className="dt-field dt-sitetiles__search"
          placeholder={t('searchSites', 'Search sites...')}
          aria-label={t('searchSites', 'Search sites...')}
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      )}
    </div>

    {sites.length === 0 ? (
      <div className="dt-statebox">
        {totalSites === 0 ? t('noSitesYet', 'No sites yet') : t('noSites', 'No sites found')}
      </div>
    ) : (
      <div className="dt-sitetiles">
        {sites.map(site => {
          const isCurrent = site.id === currentSiteId;
          return (
            <button
              key={site.id}
              type="button"
              className={`dt-sitetile${isCurrent ? ' dt-sitetile--current' : ''}`}
              aria-current={isCurrent ? 'true' : undefined}
              onClick={() => onOpenSite(site)}
            >
              <span className="dt-sitetile__text">
                <span className="dt-sitetile__name">{site.name}</span>
                {isCurrent && <span className="dt-sitetile__tag">{t('currentSite', 'Current site')}</span>}
              </span>
              <svg className="dt-sitetile__go" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </button>
          );
        })}
      </div>
    )}
  </div>
);

export default SiteTiles;
